import {Action, Selector, State, StateContext} from '@ngxs/store';
import {AutoLoginAction, AutoLoginFailed, AutoLoginSuccess, Login, LoginFailed, LoginOtpVerifiedUser, LoginSuccess, Logout, PasswordResetAction, PasswordResetRequestAction, RefreshToken, ResetLoginMessage, SetLogo, SetToken, SetUser, SetCurrentUserOperations, GetNewToken, AddOperationToTop} from './auth.actions';
import { isRequireMfa, isRequireMfaSetup, isRequirePasswordChange, isTokenObject, LoginResponse } from '@core/models/login-response.model';
import {Injectable} from '@angular/core';
import {AuthService} from '@core/services/auth.service';
import {User} from '@core/models/user.model';
import {CookieService} from 'ngx-cookie-service';
import {of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {ResourceType} from '@schedule/models/enums';
import jwtDecode from 'jwt-decode';
import {Router} from '@angular/router';
import {AttachmentModel} from '../../../modules/fuel/models/attachment.model';
import { SetSpinner } from '../shared.actions';
import { SetNotificationAction } from '../notification/notification.actions';
import { AlertDialogType } from '@shared/models';
import { Operation } from 'app/modules/organization/models/operation';
import { stat } from 'fs';
import { RefreshTokenDto } from '@core/models/refresh-token.dto';

export const authStateKey = 'auth';
export class AuthStateModel {
  public token: string;
  public user: User;
  public message: string;
  public error: boolean;
  public logo: AttachmentModel;
  public currentUserOperations: Operation[];
  public selectedOperation: Operation;
}

@State<AuthStateModel>({
  name: authStateKey,
  defaults: {
    token: null,
    user: null,
    message: null,
    error: false,
    logo: null,
    currentUserOperations: [],
    selectedOperation: null,
  },
})
@Injectable()
export class AuthState {
  constructor(
    private authService: AuthService,
    private cookieService: CookieService,
    private router: Router
  ) {}

  @Selector()
  static token(state: AuthStateModel): string | null {
    return state.token;
  }

  @Selector()
  static isAuthenticated(state: AuthStateModel): boolean {
    return !!state.token;
  }

  @Selector()
  static getUser(state: AuthStateModel): User {
    return state.user;
  }

  @Selector()
  static getLogo(state: AuthStateModel): AttachmentModel {
    return state.logo;
  }

  @Selector()
  static getOrgId(state: AuthStateModel): string {
    return state.user?.orgId;
  }

  @Selector()
  static getErrorMessage(state: AuthStateModel): string {
    return state.message || null;
  }

  @Selector()
  static hasError(state: AuthStateModel): boolean {
    return state.error;
  }

  @Selector()
  static getCurrentUserOperations(state: AuthStateModel): Operation[] {
    return [...state.currentUserOperations].sort((a, b) => {
      // Keep operations with empty id at the end
      if (!a.id || a.id === '') return 1;
      if (!b.id || b.id === '') return -1;
      
      // Sort by ID in descending order (assuming newer operations have higher IDs)
      // For UUIDs or sequential IDs, this will put newer items first
      return b.id.localeCompare(a.id);
    });
  }

  @Selector()
  static getSelectedOperation(state: AuthStateModel): Operation {
    if (state.user.operations.length > 1) {
      return { id: '', name: 'All', vehicleSpeed: null};
    }
    return state.currentUserOperations.find(
      (op) => op.id === state.user?.operations[0]
    );
  }

  @Action(SetUser)
  setUser({ patchState }: StateContext<AuthStateModel>, { user }: SetUser) {
    return of(patchState({ user }));
  }

  @Action(SetToken)
  setToken({ patchState }: StateContext<AuthStateModel>, { token }: SetToken) {
    patchState({ token });
  }

  @Action(SetLogo)
  setLogo(
    { patchState, getState }: StateContext<AuthStateModel>,
    { orgId }: SetLogo
  ) {
    const state = getState();
    return this.authService.getLogo(orgId).pipe(
      tap((logo) => {
        patchState({
          logo,
        });
      })
    );
  }

  @Action(SetCurrentUserOperations)
  setCurrentUserOperations({
    patchState,
    getState,
  }: StateContext<AuthStateModel>) {
    const state = getState();
    return this.authService.getCurrentUserOperations().pipe(
      tap((operations) => {
        patchState({
          currentUserOperations: [...operations],
        });
      })
    );
  }

  @Action(AddOperationToTop)
  addOperationToTop({
    patchState,
    getState,
  }: StateContext<AuthStateModel>, { operation }: AddOperationToTop) {
    const state = getState();
    const existingOperations = state.currentUserOperations || [];
    
    // Remove the operation if it already exists (to avoid duplicates)
    const filteredOperations = existingOperations.filter(op => op.id !== operation.id);
    
    // Add the new operation to the top
    patchState({
      currentUserOperations: [operation, ...filteredOperations],
    });
  }

  @Action(AutoLoginAction)
  autoLogin({ dispatch, patchState }: StateContext<AuthStateModel>) {
    patchState({ token: null, user: null, message: null, logo: null, error: false});
    const token = this.cookieService.get('token');
    if (!token) {
      dispatch([new AutoLoginFailed(), new SetSpinner(false)]);
      return;
    }
    try {
      const decodedToken = jwtDecode(token);
      const exp = decodedToken[`exp`] as number;

      if (exp && Date.now() > exp * 1000) {
        console.log('token expired, Auto login failed');
        dispatch([new AutoLoginFailed(), new SetSpinner(false)]);
      }
      // auto login success
      const user: User = decodedToken as User;
      user.userId = decodedToken[`orgUserId`];
      // TODO: remove this, and get it from the token, currently this is not in the token
      user.designation = ResourceType.FLEETMANAGER;
      patchState({ token, user, message: 'Login Successful', logo: null });
      dispatch([new AutoLoginSuccess(), new SetSpinner(false)]);
    } catch (e) {
      dispatch([new AutoLoginFailed(), new SetSpinner(false)]);
    }
  }

  @Action(Login)
  login(
    { dispatch, patchState }: StateContext<AuthStateModel>,
    loginAction: Login
  ) {
    dispatch(new SetSpinner(false));
    return this.authService.login({
      username: loginAction.username,
      password: loginAction.password,
      rememberMe: loginAction.rememberMe
    }).pipe(
      tap((loginResponse: LoginResponse) => {
        if (isTokenObject(loginResponse)) {
          this.handleLoginToken(loginResponse, patchState, loginAction.rememberMe);
          dispatch([new LoginSuccess(loginAction.redirectUrl), new SetSpinner(false)]);
        } else if (isRequireMfa(loginResponse)) {
          dispatch(new SetSpinner(false));
          this.router.navigate(['/auth/mfa-verify'], {
            queryParams: {
              userId: loginResponse.userId,
              mfaMethod: loginResponse.mfaMethod,
              availableMethods: (loginResponse.availableMethods || [loginResponse.mfaMethod]).join(','),
              maskedPhone: loginResponse.maskedPhone || '',
              redirect: loginAction.redirectUrl || ''
            }
          });
        } else if (isRequireMfaSetup(loginResponse)) {
          dispatch(new SetSpinner(false));
          this.router.navigate(['/auth/activate/mfa-setup'], {
            queryParams: {
              userId: loginResponse.userId,
              maskedEmail: loginResponse.maskedEmail || '',
              hasSmsOption: loginResponse.hasSmsOption ? 'true' : 'false',
              loginFlow: 'true'
            }
          });
        } else if (isRequirePasswordChange(loginResponse)) {
          dispatch([new SetSpinner(false), new SetNotificationAction(
            'Your initial password must be changed before you can log in. Please reset your password below.',
            AlertDialogType.WARNING
          )]);
          this.router.navigate(['/auth/reset-password'], {
            queryParams: { userId: loginResponse.userId }
          });
        }
      }),
      catchError((err) => dispatch([new LoginFailed(err.error?.message || err.message)]))
    );
  }

  @Action(LoginOtpVerifiedUser)
  loginOtpVerifiedUser(
    { patchState }: StateContext<AuthStateModel>,
    { accessToken, refreshToken }: LoginOtpVerifiedUser
  ) {
    const decodedToken = jwtDecode(accessToken);
    const user: User = decodedToken as User;
    user.userId = decodedToken[`orgUserId`];
    patchState({ token: accessToken, user, message: 'Login Successful' });
    this.cookieService.set('token', accessToken, decodedToken[`exp`] as number * 1000, '/');
    localStorage.setItem('currentUser', JSON.stringify(user));
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  @Action(RefreshToken)
  refresh(
    { dispatch, patchState }: StateContext<AuthStateModel>,
    action: RefreshToken
  ) {

    dispatch(new SetSpinner(true));
    const refreshToken = localStorage.getItem('refreshToken');
    const tokenRequest: RefreshTokenDto = {refreshToken, selectedOperation: action.operation?.id}

    return this.authService.refreshToken(tokenRequest).pipe(
      tap((loginResponse) => {
        this.handleLoginToken(loginResponse, patchState);
        window.location.reload();
      }),
      catchError((err) => {
        // TODO: Give a good login message
        dispatch([new LoginFailed(err.message)]);
        return dispatch(new SetSpinner(false));
      })
    );
  }

  @Action(GetNewToken)
  getNewToken(
    { dispatch, patchState }: StateContext<AuthStateModel>,
    action: GetNewToken
  ) {
    const refreshToken = localStorage.getItem('refreshToken');
    const tokenRequest: RefreshTokenDto = {refreshToken};
    const rememberMe = action.rememberMe || false;
    tokenRequest.rememberMe = rememberMe;
    return this.authService.refreshToken(tokenRequest).pipe(
      tap((loginResponse) => {
        this.handleLoginToken(loginResponse, patchState, rememberMe);
      }),
      catchError((err) => dispatch([new LoginFailed(err.error?.message || err.message)]))
    );
  }

  handleLoginToken(
    loginResponse,
    patchState: (val: Partial<AuthStateModel>) => AuthStateModel,
    rememberMe: boolean = false
  ) {
    const decodedToken = jwtDecode(loginResponse.accessToken);
    const user: User = decodedToken as User;
    user.userId = decodedToken[`orgUserId`];
    // TODO: remove this, and get it from the token, currently this is not in the token
    // user.designation = ResourceType.FLEETMANAGER;

    patchState({
      token: loginResponse.accessToken,
      user,
      message: 'Login Successful',
      logo: null,
    });
    this.cookieService.deleteAll('token');

    let expirationTime: number;
    if (decodedToken[`rememberMe`]) {
      const userLoggedTime = decodedToken[`userLoggedTime`];
      const loggedTimeMs = new Date(userLoggedTime).getTime();
      expirationTime = loggedTimeMs + (10 * 1000);
    } else {
      expirationTime = decodedToken[`exp`] * 1000;
    }
    this.cookieService.set(
      'token',
      loginResponse.accessToken,
      expirationTime,
      '/'
    );
    localStorage.setItem('currentUser', JSON.stringify(user));
    localStorage.setItem('refreshToken', loginResponse.refreshToken);
  }

  @Action(LoginFailed)
  processLoginFailure(
    { dispatch, patchState }: StateContext<AuthStateModel>,
    { errorMsg }: LoginFailed
  ) {
    dispatch([new SetSpinner(false)]);
    const isLockout = /locked|too many/i.test(errorMsg || '');
    const message = isLockout ? errorMsg : 'Username or Password Incorrect, Please try again';
    return patchState({ token: null, user: null, error: true, message, logo: null});
  }

  @Action(ResetLoginMessage)
  resetLoginMessage({ patchState }: StateContext<AuthStateModel>) {
    return patchState({ message: null, error: false});
  }

  @Action(Logout)
  logout(
    { patchState }: StateContext<AuthStateModel>,
    { redirect, currentPage }: Logout
  ) {
    const refreshToken = localStorage.getItem('refreshToken');
    const doLogout = () => {
      this.cookieService.deleteAll();
      this.cookieService.deleteAll('/');
      localStorage.clear();
      patchState({ message: null, token: null, user: null, logo: null, error: false });
    };

    if (refreshToken) {
      return this.authService.logout(refreshToken).pipe(
        catchError(() => of(null)),
        tap(() => doLogout()),
      );
    } else {
      doLogout();
    }
  }

  @Action(PasswordResetRequestAction)
  sendPasswordResetRequest({dispatch}: StateContext<AuthStateModel>, {userId}: PasswordResetRequestAction) {
    return this.authService.initResetPassword(userId)
      .pipe(
        map(r => {
          if (r.statusCode === 404) {
            return dispatch(new SetNotificationAction(r.message, AlertDialogType.ERROR));
          }
          if (r.statusCode === 429) {
            return dispatch(new SetNotificationAction(r.message, AlertDialogType.ERROR));
          }
          dispatch(new SetNotificationAction('Successfully Initialized Reset Password', AlertDialogType.SUCCESS));
          this.router.navigate(['/auth/login']);
          return r;
        }),
        catchError(err => dispatch(new SetNotificationAction('Error Initializing Reset Password', AlertDialogType.ERROR)))
      );
  }

  @Action(PasswordResetAction)
  resetPassword({dispatch}: StateContext<AuthStateModel>, {newPassword, reTypeNewPassword, resetKey}: PasswordResetAction) {
    return this.authService.resetPassword({newPassword, confirmPassword: reTypeNewPassword, resetKey})
      .pipe(
        map(r => {
          if (r && (r as any).success === false) {
            return dispatch(new SetNotificationAction((r as any).message || 'Error Resetting Password', AlertDialogType.ERROR));
          }
          dispatch(new SetNotificationAction('Successfully Reset Password', AlertDialogType.SUCCESS));
          this.router.navigate(['/auth/login']);
          return r;
        }),
        catchError(err => dispatch(new SetNotificationAction(
          err.error?.message || 'Error Resetting Password',
          AlertDialogType.ERROR
        )))
      );
  }
}
